import React, {useRef, useState, useEffect} from 'react';
import styles from '../../styles/components/LocationFlexible.module.scss';
import classnames from 'classnames';
import {stripHash, normalizeAnchorHref, parseAnchorRoom, emitRoomPreselect} from '../../utils/anchors';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import TableTooltip from './table-tooltip';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import arrowPrev from '../../assets/images/prev_arrow.svg';
import arrowNext from '../../assets/images/next_arrow.svg';
import location from '../../assets/images/location.svg';
import chevronRight from '../../assets/images/chevron-right.svg';

export default function LocationFlexible({data}) {
    const wrapperRef = useRef(null);
    const onScreen = useIntersectionObserver(wrapperRef);
    const sliderRefs = useRef({});
    const sliderPopupRef = useRef(null);
    // popup holds the list of images currently shown in the lightbox and the active index
    const [popupImages, setPopupImages] = useState(null);
    const [popupIndex, setPopupIndex] = useState(0);

    const sliderSettings = {
        dots: true,
        infinite: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
    };

    const settingsPopup = {
        dots: false,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
    };

    const handlePrevClick = (galleryKey) => {
        sliderRefs.current[galleryKey]?.slickPrev();
    };

    const handleNextClick = (galleryKey) => {
        sliderRefs.current[galleryKey]?.slickNext();
    };

    const handlePopupPrevClick = () => {
        sliderPopupRef.current?.slickPrev();
    };

    const handlePopupNextClick = () => {
        sliderPopupRef.current?.slickNext();
    };

    const openPopup = (images, index) => {
        setPopupImages(images);
        setPopupIndex(index);
    };

    const closePopup = () => {
        setPopupImages(null);
        setPopupIndex(0);
    };

    useEffect(() => {
        if (popupImages !== null) {
            sliderPopupRef.current?.slickGoTo(popupIndex);
            if (typeof window !== 'undefined' && window.document) {
                document.body.style.overflow = 'hidden';
            }
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [popupImages, popupIndex]);

    // Number of images arranged per gallery page (ACCDE / BCCDF grid)
    const GALLERY_PAGE_SIZE = 6;

    const chunkImages = (images, size) => {
        const chunks = [];
        for (let i = 0; i < images.length; i += size) {
            chunks.push(images.slice(i, i + size));
        }
        return chunks;
    };

    // Renders one collage page (grid) of images; clicking opens the lightbox
    // with the full gallery and the clicked image's global index.
    const renderGalleryPage = (pageImages, startIndex, allImages) => (
        <div className={styles.slideWrapper}>
            {pageImages.map((image, index) => {
                const globalIndex = startIndex + index;
                return (
                    <a
                        href="/#"
                        className={styles.image}
                        key={image.id ?? globalIndex}
                        onClick={(e) => {
                            e.preventDefault();
                            openPopup(allImages, globalIndex);
                        }}
                    >
                        <img src={image.url} alt={image.alt}/>
                    </a>
                );
            })}
        </div>
    );

    const renderGallery = (images, galleryKey) => {
        if (!images || images.length === 0) {
            return null;
        }

        const pages = chunkImages(images, GALLERY_PAGE_SIZE);

        // Fall back to a static grid (no slider) when everything fits on one page
        if (pages.length <= 1) {
            return (
                <div className={styles.sliderWrapper}>
                    {renderGalleryPage(images, 0, images)}
                </div>
            );
        }

        return (
            <div className={styles.sliderWrapper}>
                <div className={styles.sliderArrows}>
                    <button
                        className={styles.sliderArrow}
                        onClick={() => handlePrevClick(galleryKey)}
                    >
                        <img src={arrowPrev} alt="Zurück"/>
                    </button>
                    <button
                        className={styles.sliderArrow}
                        onClick={() => handleNextClick(galleryKey)}
                    >
                        <img src={arrowNext} alt="Weiter"/>
                    </button>
                </div>
                <Slider
                    {...sliderSettings}
                    ref={ref => {
                        sliderRefs.current[galleryKey] = ref;
                    }}
                >
                    {pages.map((page, pageIndex) => (
                        <div key={pageIndex}>
                            {renderGalleryPage(page, pageIndex * GALLERY_PAGE_SIZE, images)}
                        </div>
                    ))}
                </Slider>
            </div>
        );
    };

    // Renders a table cell value. If the text contains a fragment wrapped in
    // curly braces (e.g. "Cell content {tooltip text}"), that fragment is removed
    // from the visible text and shown as a tooltip (i) icon instead.
    const renderCellContent = (value) => {
        if (value === null || value === undefined) {
            return value;
        }

        const text = String(value);
        const match = text.match(/\{([^}]*)\}/);

        if (!match) {
            return text;
        }

        const tooltip = match[1].trim();
        const cleanText = text.replace(/\s*\{[^}]*\}\s*/, ' ').trim();

        return (
            <>
                {cleanText}
                {tooltip && <TableTooltip text={tooltip}/>}
            </>
        );
    };

    const renderBlock = (item) => {
        switch (item.type) {
            case 'text':
                return (
                    <div
                        key={item.id}
                        className={styles.textBlock}
                        dangerouslySetInnerHTML={{__html: item.text}}
                    />
                );

            case 'table':
                if (!item.table || item.table.length === 0) {
                    return null;
                }
                return (
                    <div key={item.id} className={styles.tableBlock}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Raumtyp</th>
                                    <th>Anzahl</th>
                                    <th>max. Belegung</th>
                                    <th>Fläche</th>
                                    <th title={"URL"}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {item.table.map((row) => (
                                    <tr key={row.id}>
                                        <td>{renderCellContent(row.roomtype)}</td>
                                        <td>{renderCellContent(row.amount)}</td>
                                        <td>{renderCellContent(row.occupancy)}</td>
                                        <td>{renderCellContent(row.area)}</td>
                                        <td>
                                            {row.url && (() => {
                                                const {anchor, room} = parseAnchorRoom(row.url);
                                                return (
                                                    <a href={normalizeAnchorHref(anchor)}
                                                       target={row.newTab ? '_blank' : '_self'}
                                                       rel="noopener noreferrer"
                                                       onClick={room ? () => emitRoomPreselect(room) : undefined}>
                                                        <img className={styles.anfragenIcon} src={chevronRight} alt=""/>
                                                        Jetzt anfragen
                                                    </a>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );

            case 'gallery':
                if (!item.images || item.images.length === 0) {
                    return null;
                }
                return (
                    <div key={item.id} className={styles.galleryBlock}>
                        {renderGallery(item.images, `gallery-${item.id}`)}
                    </div>
                );

            case 'image':
                if (!item.image) {
                    return null;
                }
                return (
                    <div key={item.id} className={styles.imageBlock}>
                        <a href="/#" className={styles.singleImage} onClick={(e) => {
                            e.preventDefault();
                            openPopup([item.image], 0);
                        }}>
                            <img src={item.image.url} alt={item.image.alt}/>
                        </a>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div
            className={classnames(
                styles.locationFlexibleWrapper,
                styles[data.style],
                onScreen?.isIntersecting ? styles.visible : ''
            )}
            id={stripHash(data.anchorId)}
            ref={wrapperRef}
        >
            <div className={styles.contentColumn}>
                <div className={styles.leftColumn}>
                    <div className={styles.backButton}>
                        <a href={data.backButtonURL}>{data.backButtonTitle}</a>
                    </div>

                    <div className={styles.headerWrapper}>
                        <h1 className={styles.title}>{data.title}</h1>
                        {data.address && (
                            <p className={styles.address}>
                                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(data.address)}`}
                                   target="_blank" rel="noopener noreferrer">
                                    <img src={location} alt=""/>
                                    {data.address}
                                </a>
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.rightColumn}>
                    {data.images && data.images.length > 0 && (
                        <div className={styles.topGallery}>
                            {renderGallery(data.images, 'top')}
                        </div>
                    )}

                    {data.content && data.content.map((item) => renderBlock(item))}
                </div>
            </div>

            {popupImages !== null && (
                <div className={styles.popup}>
                    <div
                        className={styles.overlay}
                        onClick={closePopup}
                    >
                        <div className={styles.popupNavButtons}
                             style={{visibility: popupImages.length <= 1 ? 'hidden' : 'visible'}}>
                            <button
                                className={styles.popupNavButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePopupPrevClick();
                                }}
                                aria-label="Vorheriges Bild"
                            >
                                <img src={arrowPrev} alt=""/>
                            </button>
                            <button
                                className={styles.popupNavButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePopupNextClick();
                                }}
                                aria-label="Nächstes Bild"
                            >
                                <img src={arrowNext} alt=""/>
                            </button>
                        </div>
                        <button
                            className={styles.closeBtn}
                            onClick={closePopup}
                        >
                            <span aria-hidden={"true"}>X</span>
                            <span className={styles.srOnly}>Popup schließen</span>
                        </button>
                    </div>
                    <div className={styles.content}>
                        <Slider ref={sliderPopupRef} {...settingsPopup}>
                            {popupImages.map((item, index) => (
                                <div className={styles.slideWrapper} key={index}>
                                    <img src={item.url} alt={item.alt}/>
                                </div>
                            ))}
                        </Slider>
                    </div>
                </div>
            )}
        </div>
    );
}
